import { apiService } from "../../../shared/services/apiService";

export interface Televisao {
    cdTelevisao: number | null;
    nmTelevisao: string;
    dsTelasCadastradas?: string | null; // Formato: "5;25;77" (IDs separados por ponto e vírgula)
    dsConfiguracoes?: string | null; // JSON com configurações: {"23": {"tempo": 30, "ordem": 1}, "95": {"tempo": 45, "ordem": 2}}
}

/**
 * Interface para configuração de uma tela no JSON
 */
export interface TelaConfig {
    tempo: number;
    ordem: number;
    /** Dias 0–6 como string (Domingo=0). Sempre normalizado para string na leitura do JSON (API pode enviar número). */
    dias?: string[];
}

export interface InserirTelevisaoBannerCommand {
    nmTelevisao: string;
}

export interface InserirTelevisaoBannerCommandResult {
    cdTelevisao: number | null;
}

export interface AtualizarTelevisaoBannerCommand {
    dsTelasCadastradas?: string; // Formato: "5;25;77" (opcional)
    dsConfiguracoes?: string; // JSON com configurações: {"23": 30, "95": 45} (opcional)
}

class TelevisaoService {
    /** Garante comparação com getDay().toString() ("0".."6") mesmo se o JSON tiver números. */
    private normalizeDiasSemana(dias: unknown): string[] {
        if (!Array.isArray(dias)) return [];
        return dias
            .filter((d) => d !== null && d !== undefined && String(d).trim() !== '')
            .map((d) => String(d));
    }

    /**
     * Lista todas as televisões cadastradas
     * Nota: Se o endpoint GET não existir ainda, retorna array vazio
     */
    async listarTelevisoes(): Promise<Televisao[]> {
        try {
            const result = await apiService.get<Televisao[] | null | any>('/v1/banner/televisao');
            
            // Se result é null, undefined ou não é um array, retorna array vazio
            if (result == null || result === undefined) {
                return [];
            }
            
            // Se não é um array, tenta converter ou retorna vazio
            if (!Array.isArray(result)) {
                // Se for um objeto único, tenta converter para array
                if (result && typeof result === 'object' && result.cdTelevisao != null) {
                    return [this.normalizeTelevisao(result)];
                }
                return [];
            }
            
            // Se é um array vazio, retorna vazio
            if (result.length === 0) {
                return [];
            }
            
            // Filtrar e normalizar televisões
            const televisoes = result
                .filter(tv => {
                    if (!tv || typeof tv !== 'object') {
                        return false;
                    }
                    // Aceitar mesmo se cdTelevisao for null temporariamente
                    return tv.nmTelevisao != null;
                })
                .map(tv => this.normalizeTelevisao(tv));
            
            return televisoes;
        } catch (error: any) {
            // Se o endpoint não existir (404) ou der erro, retorna array vazio
            const errorMessage = error?.message || String(error);
            console.warn('Erro ao listar televisões:', errorMessage);
            
            return [];
        }
    }

    /**
     * Normaliza uma televisão para garantir formato correto
     */
    private normalizeTelevisao(tv: any): Televisao {
        return {
            cdTelevisao: tv.cdTelevisao != null ? Number(tv.cdTelevisao) : null,
            nmTelevisao: String(tv.nmTelevisao || ''),
            dsTelasCadastradas: tv.dsTelasCadastradas != null ? String(tv.dsTelasCadastradas) : null,
            dsConfiguracoes: tv.dsConfiguracoes != null ? String(tv.dsConfiguracoes) : null
        };
    }

    /**
     * Busca uma televisão específica por ID
     * @param cdTelevisao - ID da televisão
     */
    async buscarTelevisao(cdTelevisao: number): Promise<Televisao> {
        const televisoes = await this.listarTelevisoes();
        const televisao = televisoes.find(t => t.cdTelevisao === cdTelevisao);
        if (!televisao) {
            throw new Error('Televisão não encontrada');
        }
        return televisao;
    }

    /**
     * Cria uma nova televisão
     * @param command - Dados da televisão a ser criada
     */
    async criarTelevisao(command: InserirTelevisaoBannerCommand): Promise<InserirTelevisaoBannerCommandResult> {
        return apiService.post<InserirTelevisaoBannerCommandResult>('/v1/banner/televisao', command);
    }

    /**
     * Atualiza as telas cadastradas e/ou configurações de uma televisão
     * @param cdTelevisao - ID da televisão
     * @param command - Dados das telas cadastradas (formato: "5;25;77") e/ou configurações (JSON)
     */
    async atualizarTelevisao(cdTelevisao: number, command: AtualizarTelevisaoBannerCommand): Promise<boolean> {
        const resultado = await apiService.put<boolean>(`/v1/banner/televisao/${cdTelevisao}`, command);
        return resultado;
    }

    /**
     * Atualiza apenas as telas cadastradas de uma televisão (método de compatibilidade)
     * @param cdTelevisao - ID da televisão
     * @param command - Dados das telas cadastradas (formato: "5;25;77")
     */
    async atualizarTelasCadastradas(cdTelevisao: number, command: AtualizarTelevisaoBannerCommand): Promise<boolean> {
        return this.atualizarTelevisao(cdTelevisao, command);
    }

    /**
     * Exclui uma televisão
     * @param cdTelevisao - ID da televisão a ser excluída
     */
    async deletarTelevisao(cdTelevisao: number): Promise<boolean> {
        return apiService.delete<boolean>(`/v1/banner/televisao/${cdTelevisao}`);
    }

    /**
     * Remove uma tela de todas as televisões onde ela está cadastrada
     * Remove tanto do campo dsTelasCadastradas quanto do dsConfiguracoes
     * @param telaId - ID da tela a ser removida
     * @returns Número de televisões atualizadas
     */
    async removerTelaDeTodasTelevisoes(telaId: number): Promise<number> {
        try {
            // Buscar todas as televisões
            const televisoes = await this.listarTelevisoes();
            
            // Filtrar apenas as televisões que têm esta tela cadastrada
            const televisoesComTela = televisoes.filter(tv => {
                if (!tv.cdTelevisao) return false;
                const telaIds = this.stringToTelaIds(tv.dsTelasCadastradas);
                return telaIds.includes(telaId);
            });

            if (televisoesComTela.length === 0) {
                return 0;
            }

            // Atualizar cada televisão
            let atualizadas = 0;
            for (const tv of televisoesComTela) {
                if (!tv.cdTelevisao) continue;

                try {
                    // Remover tela do dsTelasCadastradas
                    const telaIds = this.stringToTelaIds(tv.dsTelasCadastradas);
                    const telaIdsAtualizados = telaIds.filter(id => id !== telaId);
                    const dsTelasCadastradasAtualizado = this.telaIdsToString(telaIdsAtualizados);

                    // Remover tela do dsConfiguracoes
                    let dsConfiguracoesAtualizado = tv.dsConfiguracoes || '{}';
                    try {
                        const config = JSON.parse(dsConfiguracoesAtualizado);
                        // Remover tanto pela chave string quanto número
                        delete config[telaId.toString()];
                        delete config[telaId];
                        dsConfiguracoesAtualizado = JSON.stringify(config);
                    } catch (error) {
                        console.warn(`[televisaoService] Erro ao parsear configurações da televisão ${tv.cdTelevisao}, criando novo JSON vazio`);
                        dsConfiguracoesAtualizado = '{}';
                    }

                    // Atualizar televisão
                    await this.atualizarTelevisao(tv.cdTelevisao, {
                        dsTelasCadastradas: dsTelasCadastradasAtualizado || undefined,
                        dsConfiguracoes: dsConfiguracoesAtualizado !== '{}' ? dsConfiguracoesAtualizado : undefined
                    });

                    atualizadas++;
                } catch (error) {
                    console.error(`[televisaoService] Erro ao atualizar televisão ${tv.cdTelevisao}:`, error);
                    // Continuar com as outras televisões mesmo se uma falhar
                }
            }

            return atualizadas;
        } catch (error) {
            console.error(`[televisaoService] Erro ao remover tela ${telaId} das televisões:`, error);
            throw error;
        }
    }

    /**
     * Converte array de IDs de telas para string no formato "5;25;77"
     * @param telaIds - Array de IDs das telas
     */
    telaIdsToString(telaIds: number[]): string {
        return telaIds.filter(id => id > 0).join(';');
    }

    /**
     * Converte string no formato "5;25;77" para array de IDs
     * @param dsTelasCadastradas - String com IDs separados por ponto e vírgula
     */
    stringToTelaIds(dsTelasCadastradas?: string | null): number[] {
        if (!dsTelasCadastradas || typeof dsTelasCadastradas !== 'string' || dsTelasCadastradas.trim() === '') {
            return [];
        }
        return dsTelasCadastradas
            .split(';')
            .map(id => parseInt(id.trim(), 10))
            .filter(id => !isNaN(id) && id > 0);
    }

    /**
     * Extrai o tempo de uma tela específica do JSON de configurações
     * @param dsConfiguracoes - JSON string com configurações: {"23": {"tempo": 30, "ordem": 1}, "95": {"tempo": 45, "ordem": 2}}
     * @param telaId - ID da tela
     * @returns Tempo em segundos ou undefined se não encontrado
     */
    extrairTempoTela(dsConfiguracoes?: string | null, telaId?: number): number | undefined {
        if (!dsConfiguracoes || typeof dsConfiguracoes !== 'string' || dsConfiguracoes.trim() === '' || !telaId) {
            return undefined;
        }

        try {
            const config = JSON.parse(dsConfiguracoes);
            const telaConfig = config[telaId.toString()] || config[telaId];
            
            // Suporte para formato antigo (apenas número) e novo (objeto)
            if (typeof telaConfig === 'number' && telaConfig > 0) {
                return telaConfig;
            }
            if (telaConfig && typeof telaConfig === 'object' && typeof telaConfig.tempo === 'number' && telaConfig.tempo > 0) {
                return telaConfig.tempo;
            }
            return undefined;
        } catch (error) {
            console.warn('Erro ao extrair tempo da tela do JSON:', error);
            return undefined;
        }
    }

    /**
     * Extrai a ordem de uma tela específica do JSON de configurações
     * @param dsConfiguracoes - JSON string com configurações
     * @param telaId - ID da tela
     * @returns Ordem da tela ou undefined se não encontrado
     */
    extrairOrdemTela(dsConfiguracoes?: string | null, telaId?: number): number | undefined {
        if (!dsConfiguracoes || typeof dsConfiguracoes !== 'string' || dsConfiguracoes.trim() === '' || !telaId) {
            return undefined;
        }

        try {
            const config = JSON.parse(dsConfiguracoes);
            const telaConfig = config[telaId.toString()] || config[telaId];
            
            if (telaConfig && typeof telaConfig === 'object' && typeof telaConfig.ordem === 'number' && telaConfig.ordem > 0) {
                return telaConfig.ordem;
            }
            return undefined;
        } catch (error) {
            console.warn('Erro ao extrair ordem da tela do JSON:', error);
            return undefined;
        }
    }

    /**
     * Atualiza o tempo, ordem e/ou dias de uma tela específica no JSON de configurações
     * @param dsConfiguracoes - JSON string existente (pode ser null/undefined)
     * @param telaId - ID da tela
     * @param tempo - Tempo em segundos (opcional)
     * @param ordem - Ordem da tela (opcional)
     * @param dias - Array de dias da semana ["0", "1", ...] (opcional)
     * @returns JSON string atualizado
     */
    atualizarConfigTela(dsConfiguracoes: string | null | undefined, telaId: number, tempo?: number, ordem?: number, dias?: string[]): string {
        let config: Record<string, TelaConfig | number> = {};

        // Parse do JSON existente
        if (dsConfiguracoes && typeof dsConfiguracoes === 'string' && dsConfiguracoes.trim() !== '' && dsConfiguracoes.trim() !== '{}') {
            try {
                const parsed = JSON.parse(dsConfiguracoes);
                // Garantir que é um objeto válido
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                    config = parsed;
                }
            } catch (error) {
                console.warn('Erro ao parsear JSON de configurações, criando novo:', error);
                config = {};
            }
        }

        // Obter configuração atual da tela (pode ser formato antigo ou novo)
        const telaKey = telaId.toString();
        const configAtual = config[telaKey] || config[telaId];
        let telaConfig: TelaConfig;
        
        if (configAtual && typeof configAtual === 'object' && 'tempo' in configAtual) {
            // Formato novo: objeto com tempo, ordem e dias
            telaConfig = { ...(configAtual as TelaConfig) };
            if (!telaConfig.dias) {
                telaConfig.dias = [];
            }
        } else if (typeof configAtual === 'number') {
            // Formato antigo: apenas número (tempo)
            telaConfig = { tempo: configAtual, ordem: 0, dias: [] };
        } else {
            // Nova configuração
            telaConfig = { tempo: 0, ordem: 0, dias: [] };
        }

        // Atualizar tempo se fornecido
        if (tempo !== undefined && tempo !== null && tempo > 0) {
            telaConfig.tempo = Number(tempo);
        }

        // Atualizar ordem se fornecida
        if (ordem !== undefined && ordem !== null && ordem > 0) {
            telaConfig.ordem = Number(ordem);
        }

        // Atualizar dias se fornecidos
        if (dias !== undefined && Array.isArray(dias)) {
            telaConfig.dias = dias.filter((d: any) => d && typeof d === 'string');
        }

        // Se tempo, ordem ou dias são válidos, salvar
        if (telaConfig.tempo > 0 || telaConfig.ordem > 0 || (telaConfig.dias && telaConfig.dias.length > 0)) {
            config[telaKey] = telaConfig;
        } else {
            // Remover configuração se todos forem inválidos
            delete config[telaKey];
            delete config[telaId];
        }

        const result = JSON.stringify(config);
        return result;
    }

    /**
     * Atualiza apenas o tempo de uma tela (método de compatibilidade)
     */
    atualizarTempoTela(dsConfiguracoes: string | null | undefined, telaId: number, tempo?: number): string {
        return this.atualizarConfigTela(dsConfiguracoes, telaId, tempo, undefined);
    }

    /**
     * Extrai todos os tempos das telas do JSON de configurações
     * @param dsConfiguracoes - JSON string com configurações
     * @returns Mapa de telaId -> tempo em segundos
     */
    extrairTodosTempos(dsConfiguracoes?: string | null): Map<number, number> {
        const tempos = new Map<number, number>();

        if (!dsConfiguracoes || typeof dsConfiguracoes !== 'string' || dsConfiguracoes.trim() === '') {
            return tempos;
        }

        try {
            const config = JSON.parse(dsConfiguracoes);
            
            for (const [key, value] of Object.entries(config)) {
                const telaId = parseInt(key, 10);
                
                // Suporte para formato antigo (apenas número) e novo (objeto)
                let tempoNum: number | undefined;
                if (typeof value === 'number' && value > 0) {
                    tempoNum = value;
                } else if (value && typeof value === 'object' && 'tempo' in value) {
                    tempoNum = typeof (value as TelaConfig).tempo === 'number' ? (value as TelaConfig).tempo : undefined;
                }
                
                if (!isNaN(telaId) && telaId > 0 && tempoNum !== undefined && tempoNum > 0) {
                    tempos.set(telaId, tempoNum);
                }
            }
        } catch (error) {
            console.error('Erro ao extrair tempos do JSON:', error);
        }

        return tempos;
    }

    /**
     * Extrai todas as configurações (tempo e ordem) das telas do JSON
     * @param dsConfiguracoes - JSON string com configurações
     * @returns Mapa de telaId -> {tempo, ordem}
     */
    extrairTodasConfigs(dsConfiguracoes?: string | null): Map<number, TelaConfig> {
        const configs = new Map<number, TelaConfig>();

        if (!dsConfiguracoes || typeof dsConfiguracoes !== 'string' || dsConfiguracoes.trim() === '') {
            return configs;
        }

        try {
            const config = JSON.parse(dsConfiguracoes);
            
            for (const [key, value] of Object.entries(config)) {
                const telaId = parseInt(key, 10);
                
                if (isNaN(telaId) || telaId <= 0) continue;
                
                // Suporte para formato antigo (apenas número) e novo (objeto)
                if (typeof value === 'number' && value > 0) {
                    // Formato antigo: apenas tempo, ordem será 0, dias vazio
                    configs.set(telaId, { tempo: value, ordem: 0, dias: [] });
                } else if (value && typeof value === 'object' && 'tempo' in value) {
                    const telaConfig = value as TelaConfig;
                    // CRÍTICO: SEMPRE ler a ordem - SEMPRE vai ter uma ordem
                    // A ordem pode vir como número ou string, garantir que seja número
                    let ordem = 0;
                    if (typeof telaConfig.ordem === 'number') {
                        ordem = telaConfig.ordem;
                    } else if (typeof telaConfig.ordem === 'string') {
                        ordem = parseInt(telaConfig.ordem, 10) || 0;
                    }
                    configs.set(telaId, {
                        tempo: typeof telaConfig.tempo === 'number' ? telaConfig.tempo : 0,
                        ordem: ordem,
                        dias: this.normalizeDiasSemana(telaConfig.dias)
                    });
                } else if (value && typeof value === 'object' && ('ordem' in value || 'dias' in value)) {
                    // Pode ter apenas ordem ou dias, sem tempo
                    const telaConfig = value as any;
                    // CRÍTICO: SEMPRE ler a ordem - SEMPRE vai ter uma ordem
                    // A ordem pode vir como número ou string, garantir que seja número
                    let ordem = 0;
                    if (typeof telaConfig.ordem === 'number') {
                        ordem = telaConfig.ordem;
                    } else if (typeof telaConfig.ordem === 'string') {
                        ordem = parseInt(telaConfig.ordem, 10) || 0;
                    }
                    configs.set(telaId, {
                        tempo: 0,
                        ordem: ordem,
                        dias: this.normalizeDiasSemana(telaConfig.dias)
                    });
                }
            }
        } catch (error) {
            console.error('Erro ao extrair configurações do JSON:', error);
        }

        return configs;
    }
}

export const televisaoService = new TelevisaoService();
